import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

const SKELETON_ROWS = ["alpha", "bravo", "charlie", "delta"];

const formatCurrency = (value: number) =>
	new Intl.NumberFormat("pt-PT", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 2,
	}).format(value);

const normalizeNumber = (value: number | null | undefined) => {
	const parsed = Number(value ?? 0);
	return Number.isNaN(parsed) ? 0 : parsed;
};

type AllRequestsListProps = {
	data: Array<{
		id?: string | number | null;
		material: {
			brand?: string | null;
			model?: string | null;
			description?: string | null;
			storeBox?: string | null;
			price?: number | null;
		};
		total?: number | null;
		price?: number | null;
		observation?: string | null;
	}>;
	loading?: boolean;
};

type MaterialSummary = {
	brand?: string | null;
	model?: string | null;
	description?: string | null;
	storeBox?: string | null;
	price?: number | null;
};

const buildMaterialLabel = (material: MaterialSummary) => {
	const brand = material.brand?.trim() ?? "";
	const model = material.model?.trim() ?? "";
	const description = material.description?.trim() ?? "";
	const parts = [brand, model, description].filter((value) => value.length > 0);
	return parts.join(" ") || "-";
};

export function AllRequestsList({
	data,
	loading = false,
}: AllRequestsListProps) {
	const renderSkeleton = () => (
		<TableBody>
			{SKELETON_ROWS.map((rowKey) => (
				<TableRow
					key={`all-requests-skeleton-${rowKey}`}
					className="animate-pulse"
				>
					<TableCell>
						<div className="h-4 w-56 rounded bg-muted/70" />
					</TableCell>
					<TableCell className="text-center">
						<div className="mx-auto h-4 w-10 rounded bg-muted/60" />
					</TableCell>
					<TableCell className="text-center">
						<div className="mx-auto h-4 w-12 rounded bg-muted/50" />
					</TableCell>
					<TableCell className="text-center">
						<div className="mx-auto h-4 w-20 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-32 rounded bg-muted/40" />
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);

	const renderEmpty = () => (
		<TableBody>
			<TableRow>
				<TableCell
					colSpan={5}
					className="py-10 text-center text-sm text-muted-foreground"
				>
					Sem pedidos para mostrar.
				</TableCell>
			</TableRow>
		</TableBody>
	);

	const renderRows = () => (
		<TableBody>
			{data.map((entry) => {
				const quantity = entry.total ?? 0;
				const priceValue =
					entry.price ?? entry.material.price ?? normalizeNumber(entry.price);
				const rowKey =
					entry.id ?? `${entry.material.description ?? "material"}-${quantity}`;

				return (
					<TableRow key={rowKey}>
						<TableCell>{buildMaterialLabel(entry.material)}</TableCell>
						<TableCell className="text-center">{quantity}</TableCell>
						<TableCell className="text-center">
							{entry.material.storeBox?.trim() || "-"}
						</TableCell>
						<TableCell className="text-center">
							{formatCurrency(normalizeNumber(priceValue))}
						</TableCell>
						<TableCell>{entry.observation || "-"}</TableCell>
					</TableRow>
				);
			})}
		</TableBody>
	);

	return (
		<div className="overflow-hidden rounded-lg border border-border/60 bg-card/70">
			<Table>
				<TableHeader>
					<TableRow className="bg-muted/50">
						<TableHead>Descrição</TableHead>
						<TableHead className="w-[80px] text-center">Qtn.</TableHead>
						<TableHead className="w-[90px] text-center">Caixa</TableHead>
						<TableHead className="w-[150px] text-center">
							Preço unidade
						</TableHead>
						<TableHead>Observação</TableHead>
					</TableRow>
				</TableHeader>
				{loading
					? renderSkeleton()
					: data.length > 0
						? renderRows()
						: renderEmpty()}
			</Table>
		</div>
	);
}
